import { useMutation } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useCallback } from 'react';

import { OutlookMailboxSettings, type EmailGroup } from './outlook-mailbox-settings';
import { SettingsCard } from './settings-card';
import { useSettingsPanelContext } from './settings-panel-context';
import { ACQUIRE_NOVA_MICROSOFT_GRAPH_DELEGATED_TOKEN } from '../../../queries/mutations';
import { MSGraphToggle } from '../../ms-graph';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useMSGraphAuth } from '../contexts/ms-graph-context';
import { useChatSettings } from '../hooks/use-chat-settings';

interface GraphDirectoryObject {
  deletedDateTime?: string | null;
  displayName?: string | null;
  hideFromOutlookClients?: boolean | null;
  id?: string | null;
  isArchived?: boolean | null;
  mail?: string | null;
  mailEnabled?: boolean | null;
}

interface GraphMemberOfResponse {
  value?: GraphDirectoryObject[];
}

const GRAPH_MEMBER_OF_URL =
  'https://graph.microsoft.com/v1.0/me/memberOf' +
  '?$select=id,displayName,mail,deletedDateTime,isArchived,mailEnabled,hideFromOutlookClients';

async function fetchMailboxOptionsWithAccessToken(accessToken: string): Promise<EmailGroup[]> {
  // The delegated feature flag only changes how we obtain a Graph token. The
  // Manage UI still reads the same mailbox options from Microsoft Graph so the
  // save path and selected mailbox settings remain unchanged.
  const response = await fetch(GRAPH_MEMBER_OF_URL, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load Microsoft Graph mailbox options: ${response.status}`);
  }

  const result = (await response.json()) as GraphMemberOfResponse;

  return (
    result.value
      ?.filter(
        (group) =>
          group.mailEnabled === true &&
          group.mail != null &&
          group.mail !== '' &&
          group.hideFromOutlookClients !== true &&
          group.isArchived !== true &&
          group.deletedDateTime == null &&
          group.id != null &&
          group.id !== '',
      )
      .map((group) => ({
        displayName: group.displayName || 'Unnamed Group',
        id: group.id!,
        mail: group.mail!,
      })) ?? []
  );
}

function OutlookManageView({ onBack }: { onBack: () => void }) {
  const delegatedGraphConnectionsEnabled = useFeatureFlag('nova-newfront-msal-delegated-graph-connections', false);
  const [acquireNovaMicrosoftGraphDelegatedToken] = useMutation(ACQUIRE_NOVA_MICROSOFT_GRAPH_DELEGATED_TOKEN);
  const msGraphAuth = useMSGraphAuth();
  const graphClient = msGraphAuth?.graphClient ?? null;
  const { serverSettings, persistSettings } = useChatOverlayContext();
  const { backHandlerRef } = useSettingsPanelContext();
  const notify = useNotify();
  const loadDelegatedGroups = useCallback(async () => {
    // For delegated auth, the browser asks BFF for a short-lived delegated
    // access token backed by users-service's MSAL token cache. We deliberately
    // do not persist this token in browser state; it is used immediately for
    // mailbox discovery and then discarded.

    const { data } = await acquireNovaMicrosoftGraphDelegatedToken();

    const tokenResult = data?.acquireNovaMicrosoftGraphDelegatedToken;

    if (!tokenResult?.accessToken) {
      throw new Error(`Microsoft Graph authorization is required: ${tokenResult?.status ?? 'unknown'}`);
    }

    return fetchMailboxOptionsWithAccessToken(tokenResult.accessToken);
  }, [acquireNovaMicrosoftGraphDelegatedToken]);

  const chatSettings = useChatSettings({
    // Keep the original MSAL-browser graphClient path for Manage when the
    // delegated flag is off. With the flag on, useChatSettings receives a
    // loader function instead, but its selected mailbox persistence is shared.
    graphClient: delegatedGraphConnectionsEnabled ? null : graphClient,
    loadMailboxGroups: delegatedGraphConnectionsEnabled ? loadDelegatedGroups : undefined,
    serverSelectedGroupIds: serverSettings?.outlookConnectedEmailGroupIds,
    serverSharedInboxes: serverSettings?.outlookSharedInboxes,
    onSave: ({ groupIds, sharedInboxes }) =>
      persistSettings({ outlookConnectedEmailGroupIds: groupIds, outlookSharedInboxes: sharedInboxes }),
  });

  return (
    <div className="flex h-full flex-col">
      <OutlookMailboxSettings
        {...chatSettings}
        onCancel={onBack}
        onSaveSuccess={() => {
          notify.success('Settings saved');
          onBack();
        }}
        cancelRef={backHandlerRef}
        className="px-5"
      />
    </div>
  );
}

function CountBadge({ count }: { count: number }) {
  return (
    <span
      className="inline-flex h-5 min-w-8 items-center justify-center rounded-full bg-[#E2EBF1] px-2 font-['Open_Sans',sans-serif] text-[12px] leading-5 font-semibold tracking-wider text-[#257FFB]"
      data-testid="outlook-settings-count-badge"
    >
      {count}
    </span>
  );
}

/**
 * Outlook Connect settings component that displays a toggle switch for enabling/disabling Outlook Connect.
 * Uses MSGraphToggle primitive to manage Outlook Connect state.
 * When connected, shows a summary with badge counts and a "Manage" link.
 */
export function OutlookConnectSettings() {
  const isMSGraphAuthEnabled = useFeatureFlag('nova-tools-graph-api', false);
  const delegatedGraphConnectionsEnabled = useFeatureFlag('nova-newfront-msal-delegated-graph-connections', false);
  const { outlookConnectEnabled, serverSettings, canConnectOutlook } = useChatOverlayContext();
  const { pushView, popView } = useSettingsPanelContext();

  if ((!isMSGraphAuthEnabled && !delegatedGraphConnectionsEnabled) || !canConnectOutlook) {
    return null;
  }

  const emailGroupCount = serverSettings?.outlookConnectedEmailGroupIds?.length ?? 0;
  const sharedInboxCount = serverSettings?.outlookSharedInboxes?.length ?? 0;

  return (
    <SettingsCard title="Outlook Connect" action={<MSGraphToggle />}>
      <div className="flex flex-col gap-2.5">
        <p className="font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
          Allow Nova to access your Outlook inbox
        </p>

        {outlookConnectEnabled && (
          <div className="flex items-start justify-between rounded-[6px] bg-[#FAFBFC] px-5 py-4">
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2.5">
                <CountBadge count={emailGroupCount} />
                <span className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#262D46]">
                  Email Groups
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <CountBadge count={sharedInboxCount} />
                <span className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#262D46]">
                  Shared inboxes
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => pushView(<OutlookManageView onBack={popView} />)}
              className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#0957C3]"
              data-testid="outlook-settings-manage"
            >
              Manage
            </button>
          </div>
        )}
      </div>
    </SettingsCard>
  );
}
